import * as React from 'preact/compat'
import cn from 'classnames'
import SVG from '../SVG'
import formCss from '../../../css/form.pcss.json'
import buttonsCss from '../../../css/buttons.pcss.json'
import type { TargetedEvent } from 'preact/compat'
import { captureException } from '../../common/helpers'
import { showToast } from '../toast'

function translit(value: string) {
  value = value.toLowerCase()
  const replace = {
    '&amp;': 'and',
    '&quot;': '',
    '&nbsp;': '',
    '&': 'and',
    '\'': '',
    '.': '',
    'а': 'a',
    'б': 'b',
    'в': 'v',
    'г': 'g',
    'д': 'd',
    'е': 'e',
    'ж': 'zh',
    'з': 'z',
    'и': 'i',
    'й': 'y',
    'к': 'k',
    'л': 'l',
    'м': 'm',
    'н': 'n',
    'о': 'o',
    'п': 'p',
    'р': 'r',
    'с': 's',
    'т': 't',
    'у': 'u',
    'ф': 'f',
    'х': 'h',
    'ц': 'c',
    'ч': 'ch',
    'ш': 'sh',
    'щ': 'sch',
    'ъ': '',
    'ы': 'y',
    'ь': '',
    'э': 'e',
    'ю': 'yu',
    'я': 'ya',
    'і': 'i',
    'ї': 'yi',
    'є': 'e'
  };

  for (const find in replace) {
    const regex = new RegExp(find, 'g');
    value = value.replace(regex, replace[find]);
  }

  value = value.replace(/[^A-Za-z0-9_-]/g, '_')
    .replace(/[_-]+/g, '_')
    .replace(/^[_-]/g, '')
    .replace(/[_-]$/g, '')

  return value;
}

interface Title {
  TitleId: number | string,
  GameName: string,
  GameKeywords: string,
  GameNameUrl: string,
  GameNamePrimary: boolean,
  remove_from_search: boolean
}

interface EditorPropTypes {
  title: Title,
  updateTitle: (title: Title) => void,
  setPrimary: (titleId: Title['TitleId']) => void,
  removeTitle: (titleId: Title['TitleId']) => void,
  setRemoveFromSearch: (titleId: Title['TitleId'], value: boolean) => void,
  titlesCount: number,
  checkName: (name: string, titleId: number) => Promise<boolean>,
  checkUrl: (url: string, titleId: number) => Promise<boolean>,
  addError: (field: string) => void,
  removeError: (field: string) => void,
}

interface EditorStateTypes {
  isActive: boolean,
  nameError?: string,
  urlError?: string,
  initialTitle?: string,
  initialUrl?: string,
}

class GameTitleEditor extends React.Component<EditorPropTypes, EditorStateTypes> {
  state: EditorStateTypes = {
    isActive: typeof this.props.title.TitleId === 'string' && this.props.title.TitleId.startsWith('new'),
    initialTitle: this.props.title.GameName,
    initialUrl: this.props.title.GameNameUrl,
  }

  setPrimary = () => {
    this.props.setPrimary(this.props.title.TitleId)
  }

  setRemoveFromSearch = (event: TargetedEvent<HTMLInputElement>) => {
    const target = event.currentTarget
    this.props.setRemoveFromSearch(this.props.title.TitleId, target.checked)
  }

  showEditor = () => {
    this.setState({ isActive: true })
  }

  hideEditor = () => {
    this.setState({ isActive: false })
  }

  updateGameName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { title, updateTitle } = this.props
    const { value } = e.currentTarget
    const newTitle = {
      ...title,
    }
    newTitle.GameName = value.replace('`', "'").replace('’', "'")

    if (typeof title.TitleId === 'string' && title.TitleId.startsWith('new')) {
      newTitle.GameNameUrl = translit(value)
      // noinspection JSIgnoredPromiseFromCall
      this.checkUrl(newTitle.GameNameUrl)
    }

    updateTitle(newTitle)
    // noinspection JSIgnoredPromiseFromCall
    this.checkName(value)
  }

  updateGameKeywords = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { title, updateTitle } = this.props
    const { value } = e.currentTarget
    const newTitle = {
      ...title,
      GameKeywords: value,
    }
    updateTitle(newTitle)
  }

  updateUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { title, updateTitle } = this.props
    const { value } = e.currentTarget
    const newTitle = {
      ...title,
      GameNameUrl: value,
    }
    updateTitle(newTitle)
    // noinspection JSIgnoredPromiseFromCall
    this.checkUrl(value)
  }

  checkName = async (value: string) => {
    const { title, checkName, addError, removeError } = this.props
    const { initialTitle } = this.state

    let titleId = null
    if (typeof title.TitleId === 'number') {
      titleId = title.TitleId
    }

    if (value !== initialTitle && await checkName(value, titleId)) {
      this.setState({
        nameError: 'Игра с таким названием уже существует'
      })
      addError(`gameName[${titleId}]`)
      return
    }
    this.setState({
      nameError: null
    })
    removeError(`gameName[${titleId}]`)
  }

  checkUrl = async (value: string) => {
    const { title, checkUrl, addError, removeError } = this.props
    const { initialTitle } = this.state

    let titleId = null
    if (typeof title.TitleId === 'number') {
      titleId = title.TitleId
    }

    if (value !== initialTitle && await checkUrl(value, titleId)) {
      this.setState({
        urlError: 'Игра с такой ссылкой уже существует'
      })
      addError(`gameUrl[${titleId}]`)
      return
    }
    this.setState({
      urlError: null
    })
    removeError(`gameUrl[${titleId}]`)
  }

  isNew = () => {
    return typeof this.props.title.TitleId === 'string' && this.props.title.TitleId.startsWith('new')
  }

  removeTitle = () => {
    this.props.removeTitle(this.props.title.TitleId)
  }

  copyName = async () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(this.props.title.GameName)
        .then(() => {
          showToast({
            type: 'success',
            text: 'Название скопировано'
          })
        })
        .catch((e) => {
          captureException('Game Titles Editor', e)
          showToast({
            type: 'error',
            text: 'При копировании названия произошла ошибка'
          })
        })
    } else {
      const textarea = document.createElement('textarea')
      await navigator.clipboard.writeText(this.props.title.GameName)
      document.body.removeChild(textarea)
      showToast({
        type: 'success',
        text: 'Название скопировано'
      })
    }
  }

  render() {
    const { title, titlesCount } = this.props
    const { isActive, nameError, urlError } = this.state

    return <div style='width: 100%'>
      <div style='display: flex; column-gap: 16px; align-items: center'>
        <styled-checkbox
          label={title.GameName}
          checked={title.GameNamePrimary}
          onChange={this.setPrimary}
        />
        <button
          type='button'
          className={cn(buttonsCss['low-contrast'], buttonsCss['button--icon'])}
          style='width: 32px; height: 32px; padding: 6px !important'
          onClick={this.copyName}
        >
          <SVG i='sg-new/copy' adaptive/>
        </button>
        {!isActive && <button
          type='button'
          className={cn(buttonsCss['low-contrast'], buttonsCss['button--icon'])}
          style='width: 32px; height: 32px; padding: 6px !important;'
          onClick={this.showEditor}
        >
          <SVG i='sg-new/edit' adaptive/>
        </button>}
      </div>
      <div style={{ display: isActive ? 'block' : 'none', marginBottom: '16px' }}>
        <div className={formCss['form-group']} style='margin-bottom: 16px'>
          <label className={formCss['label']}>Название игры</label>
          <input
            type='text' className={cn({
            [formCss['input']]: true,
            [formCss['input--error']]: nameError,
          })} name={`GameName[${title.TitleId}][GameName]`} value={title.GameName} onInput={this.updateGameName}
          />
          {!this.isNew() &&
            <span className={formCss['error-hint']}>Не рекомендуется менять — влияет на поисковую оптимизацию</span>}
          {nameError && <span className={formCss['error-hint']}>{nameError}</span>}
        </div>
        <div className={formCss['form-group']} style='margin-bottom: 16px'>
          <label className={formCss['label']}>Ключевые слова</label>
          <input
            type='text'
            className={formCss['input']}
            name={`GameName[${title.TitleId}][GameKeywords]`}
            value={title.GameKeywords}
            onInput={this.updateGameKeywords}
          />
        </div>
        <div className={formCss['form-group']} style='margin-bottom: 16px'>
          <label className={formCss['label']}>Ссылка</label>
          <input
            type='text' className={cn({
            [formCss['input']]: true,
            [formCss['input--error']]: urlError,
          })} name={`GameName[${title.TitleId}][GameNameUrl]`} value={title.GameNameUrl} onInput={this.updateUrl}
          />
          {!this.isNew() &&
            <span className={formCss['hint']} style='color: var(--brand-red)'>Не рекомендуется менять — влияет на поисковую оптимизацию</span>}
          {urlError && <span className={formCss['error-hint']}>{urlError}</span>}
        </div>
        <input
          type='hidden'
          name={`GameName[${title.TitleId}][GameNamePrimary]`}
          value={title.GameNamePrimary ? '1' : '0'}
        />
        <styled-checkbox
          name={`GameName[${title.TitleId}][remove_from_search]`}
          label='Скрыть из поиска'
          checked={!!title.remove_from_search}
          onChange={this.setRemoveFromSearch}
          style='margin-bottom: 16px'
        />
        <div style='display: flex; column-gap: 16px'>
          {(!this.isNew() || titlesCount > 1) &&
            <button type='button' className={buttonsCss['primary']} onClick={this.hideEditor}>
              Скрыть редактор
            </button>}
          {this.isNew() && titlesCount > 1 &&
            <button type='button' className={buttonsCss['outline']} onClick={this.removeTitle}>
              Удалить
            </button>}
        </div>
      </div>
    </div>
  }
}

interface PropTypes {
  titles: Array<Title>,
  updateTitles: (titles: Array<Title>) => void,
  checkName: (name: string, titleId: number) => Promise<boolean>,
  checkUrl: (url: string, titleId: number) => Promise<boolean>,
  addError: (field: string) => void,
  removeError: (field: string) => void,
}

interface StateTypes {
  titles: Array<Title>
}

export default class GameTitlesEditor extends React.Component<PropTypes, StateTypes> {

  newCounter = 0

  state: StateTypes = {
    titles: this.props.titles.length > 0 ? this.props.titles : [{
      TitleId: 'new-' + this.newCounter++,
      GameName: '',
      GameKeywords: '',
      GameNameUrl: '',
      GameNamePrimary: true,
      remove_from_search: false
    }]
  }

  componentDidUpdate(previousProps: Readonly<PropTypes>) {
    if (this.props.titles !== previousProps.titles) {
      this.setState({ titles: this.props.titles })
    }
  }

  addNewTitle = () => {
    this.setState({
      titles: [...this.state.titles, {
        TitleId: 'new-' + this.newCounter++,
        GameName: '',
        GameKeywords: '',
        GameNameUrl: '',
        GameNamePrimary: false,
        remove_from_search: false
      }]
    })
  }

  updateTitle = (title: Title) => {
    const { titles } = this.state
    const index = titles.findIndex(t => t.TitleId === title.TitleId)
    titles[index] = title
    this.setState({ titles })
    this.props.updateTitles(titles)
  }

  setPrimary = (titleId: Title['TitleId']) => {
    const titles = [...this.state.titles]
    titles.forEach(t => t.GameNamePrimary = t.TitleId === titleId)
    this.setState({ titles })
  }

  setRemoveFromSearch = (titleId: Title['TitleId'], value: boolean) => {
    const titles = [...this.state.titles]
    titles.forEach(title => {
      if (title.TitleId === titleId) {
        title.remove_from_search = value
      }
    })
    this.setState({ titles })
  }

  removeTitle = (titleId: Title['TitleId']) => {
    const titles = this.state.titles.filter(t => t.TitleId !== titleId)
    this.setState({ titles })
  }

  render() {
    const { checkName, checkUrl, addError, removeError } = this.props
    const { titles } = this.state

    return <div style='display: flex; flex-direction: column; row-gap: 8px; align-items: start;'>
      <p className={formCss['hint']}>«Галочка» обозначает основное название, которое будет отображаться на сайте</p>
      {titles.map(title => <GameTitleEditor
        key={title.TitleId}
        title={title}
        updateTitle={this.updateTitle}
        setPrimary={this.setPrimary}
        removeTitle={this.removeTitle}
        setRemoveFromSearch={this.setRemoveFromSearch}
        titlesCount={titles.length}
        checkName={checkName}
        checkUrl={checkUrl}
        addError={addError}
        removeError={removeError}
      />)}
      <button type='button' className={buttonsCss['low-contrast']} onClick={this.addNewTitle}>Добавить название</button>
    </div>
  }
}
